import Divider from 'components/Divider';
import { observer } from 'mobx-react-lite';

import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { useNavigate } from 'react-router-dom';
import { useModal } from 'hooks/useModal';
import { updateOrder } from 'api/accessexpressv3/api';
import MissingServicesModal from '../Locations/MissingServicesModal';
import ClearAllModal from '../ClearAllModal';
import SaveAsDraft from './SaveAsDraft';
import { ShippingAssignmentModal } from '../UnassignedProducts/ShippingAssignmentModal';
import { UnassignedProducts } from '../UnassignedProducts/UnassignedProducts';
import { buildItemPayload } from 'screens/AccessExpressV3/utils';

const BasketFooter = observer(() => {
  const cart = useCartContext();
  const navigate = useNavigate();

  const goToAccessCheckout = () => {
    navigate('/access-express/v3/checkout/access');
  };

  const handleCheckout = () => {
    if (
      cart.unassignedPrimaryMobilityItems.length &&
      cart.shippingAssignmentMethod !== 'manual' &&
      cart.locations.length > 0
    ) {
      shippingAssignmentModalOpen();
      return;
    }

    if (cart.locationMissingServices.length) {
      missingServicesModalOpen();
      return;
    }

    goToAccessCheckout();
  };

  const { open: missingServicesModalOpen, ...missingServicesModalProps } =
    useModal();
  const { open: shippingAssignmentModalOpen, ...shippingAssignmentModalProps } =
    useModal();

  const onProceedToCheckout = async () => {
    cart.setLoadingDraftQuote(true);
    cart.removeLocationsMissingServices();
    const dataForPostOrUpdate = {
      additional_details: cart.additionalDetails ?? {},
      locations: cart.locations.map((location) => {
        const order_items = cart
          .itemsByLocation(location.id)
          .filter((item) => item.category === 'Access')
          .map(buildItemPayload);
        return {
          address: {
            ...location,
            id: location.id as string,
          },
          order_items,
          id: location.id as string,
        };
      }),
    };

    try {
      const draft = await updateOrder(dataForPostOrUpdate, cart.draftId!);
      cart.updateStateFromDraft(draft);
      cart.setLoadingDraftQuote(false);
    } catch (error) {
      console.error(error);
      cart.setLoadingDraftQuote(false);
    }

    goToAccessCheckout();
  };

  const handleClearAll = () => {
    cart.clearAll();
  };

  const syncCartWithBackend = async () => {
    if (cart.draftId) {
      cart.setLoadingDraftQuote(true);
      // NOTE: This logic is duplicated across multiple components (handleLocationsChange, handleFindServices).
      const dataForPostOrUpdate = {
        additional_details: cart.additionalDetails ?? {},
        locations: cart.locations.map((location) => {
          const order_items = cart
            .itemsByLocation(location.id)
            .filter((item) => item.category === 'Access')
            .map(buildItemPayload);
          return {
            address: {
              ...location,
              id: location.id as string,
            },
            order_items,
            id: location.id as string,
          };
        }),
      };
      const draft = await updateOrder(dataForPostOrUpdate, cart.draftId);
      cart.updateStateFromDraft(draft);
      cart.setLoadingDraftQuote(false);
    }
  };

  const handleDone = () => {
    cart.doneEditingLocations();
    syncCartWithBackend();
  };

  const handleCancel = () => {
    cart.cancelEditingLocations();
  };

  const cartHasMobilityItems =
    cart.locations.length > 0 && cart.unassignedPrimaryMobilityItems.length > 0;

  const handleShippingAssignmentConfirmation = (
    method: 'checkout' | 'manual',
  ) => {
    cart.setShippingAssignmentMethod(method);
    shippingAssignmentModalProps.close();

    if (method === 'checkout') {
      goToAccessCheckout();
    }
  };

  return (
    <div className="flex flex-col gap-8">
      {cart.isEditingLocations ? (
        <div className="flex gap-4">
          <GraniteButton
            variant="secondary"
            className="w-full"
            onClick={handleCancel}
          >
            Cancel
          </GraniteButton>
          <GraniteButton
            variant="tertiary"
            className="w-full"
            onClick={handleDone}
            disabled={cart.locations.length === 0}
          >
            Done
          </GraniteButton>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-8">
            {cart.shippingAssignmentMethod === 'manual' && (
              <Divider className="" />
            )}
            {cartHasMobilityItems && <UnassignedProducts />}
            {cart.shippingAssignmentMethod !== 'manual' && (
              <div className="flex flex-col gap-2">
                <Divider />
                <div className="flex justify-between">
                  <GraniteButton
                    size="small"
                    variant="ghost"
                    className="hover:!border-accent-primary hover:!text-accent-primary"
                    onClick={() => cart.editLocations()}
                  >
                    Edit locations
                  </GraniteButton>
                  <ClearAllModal
                    onClick={handleClearAll}
                    disabled={cart.items.length === 0}
                  />
                </div>
              </div>
            )}
          </div>
          {!cart.resumeOrder?.id || cart.totalQuantity !== 0 ? (
            <div className="flex flex-col gap-4">
              {cart.totalQuantity !== 0 && (
                <GraniteButton
                  variant="tertiary"
                  onClick={handleCheckout}
                  disabled={
                    cart.loadingDraftForPricing || cart.loadingDraftQuote
                  }
                >
                  Checkout ({cart.totalQuantity})
                </GraniteButton>
              )}
              {cart.shippingAssignmentMethod !== 'manual' ? (
                <SaveAsDraft />
              ) : (
                <GraniteButton
                  variant="secondary"
                  onClick={() => cart.resetShippingAssignmentMethod()}
                >
                  Back
                </GraniteButton>
              )}
            </div>
          ) : null}
          <MissingServicesModal
            {...missingServicesModalProps}
            onProceedToCheckout={onProceedToCheckout}
          />
          <ShippingAssignmentModal
            {...shippingAssignmentModalProps}
            onConfirmation={handleShippingAssignmentConfirmation}
          />
        </>
      )}
    </div>
  );
});

export default BasketFooter;
